import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  useTheme, 
  Divider,
  Grid,
  alpha
} from '@mui/material';
import { 
  LockOutlined, 
  ArrowBack as ArrowBackIcon, 
  Home as HomeIcon, 
  SupportAgent as SupportIcon,
  ErrorOutline as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const UnauthorizedPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        py: { xs: 4, md: 8 }
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={0} sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          backgroundColor: 'background.default',
          boxShadow: 'none'
        }}>
        
          <Grid item xs={12} md={5} sx={{ 
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            p: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}>
          
            <Box 
              sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: 8,
                bgcolor: 'primary.main',
                opacity: 0.8
              }} 
            />
            
          
            <Box sx={{ 
              width: 140,
              height: 140,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              position: 'relative'
            }}>
              <Box sx={{ 
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                position: 'absolute'
              }} />
              <Box sx={{ 
                width: '85%',
                height: '85%',
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                position: 'absolute'
              }} />
              <Box sx={{ 
                width: 100,
                height: 100,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.contrastText',
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.5)}`
              }}>
                <LockOutlined sx={{ fontSize: 50 }} />
              </Box>
            </Box>
            
            <Typography variant="h2" align="center" color="primary.main" sx={{ fontWeight: 700, mb: 1 }}>
              403
            </Typography>
            <Typography variant="h5" align="center" color="text.primary" sx={{ mb: 3, fontWeight: 500 }}>
              Access Denied
            </Typography>
            
            <Divider sx={{ width: '50%', my: 3, display: { xs: 'block', md: 'none' } }} />
          </Grid>
          
         
          <Grid item xs={12} md={7} sx={{ 
            p: { xs: 4, md: 6 },
            backgroundColor: 'background.default',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
                You don't have permission
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 560 }}>
                We're sorry, but you don't have the necessary permissions to access this page.
                This area is restricted and requires specific authorization.
              </Typography>
              
              <Box 
                sx={{ 
                  display: 'flex', 
                  p: 2, 
                  my: 3, 
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  borderRadius: 1
                }}
              >
                <ErrorIcon color="primary" sx={{ mr: 2, alignSelf: 'flex-start', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Why am I seeing this?
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Your user account doesn't have the required role or permissions
                    </Typography>
                  
                    <Typography component="li" variant="body2" color="text.secondary">
                      You might need to request access from your administrator
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ mt: 'auto' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<HomeIcon />}
                    onClick={() => navigate('/')}
                    sx={{ 
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: 2
                    }}
                  >
                    Return to Home
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ 
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: 2
                    }}
                  >
                    Go Back
                  </Button>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  color="primary"
                  startIcon={<SupportIcon />}
                  onClick={() => navigate('/support')}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Contact Support
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
        
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4, opacity: 0.7 }}>
          Error reference: 403-UA-{Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}
        </Typography>
      </Container>
    </Box>
  );
};

export default UnauthorizedPage;